import React, { useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Link,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import type { BillingRecord } from "@services/billing";

const RESTORE_STATS = [
  { value: "47", label: "Compliance records" },
  { value: "14", label: "Open compliance items" },
  { value: "3", label: "Upcoming deadlines" },
  { value: "2", label: "Regulatory updates" },
] as const;

type ResubscribeDialogProps = {
  open: boolean;
  record: BillingRecord | null;
  submitting: boolean;
  onBackToBilling: () => void;
  onConfirm: () => void;
  onChangePaymentMethod?: () => void;
};

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const trimmed = String(value).trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }
  const slash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (slash) {
    return new Date(Number(slash[3]), Number(slash[1]) - 1, Number(slash[2]));
  }
  const d = new Date(trimmed);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatShortDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function addMonths(date: Date, months: number): Date {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

function daysUntil(target: Date): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(target);
  end.setHours(0, 0, 0, 0);
  return Math.max(
    0,
    Math.round((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)),
  );
}

export const ResubscribeDialog: React.FC<ResubscribeDialogProps> = ({
  open,
  record,
  submitting,
  onBackToBilling,
  onConfirm,
  onChangePaymentMethod,
}) => {
  const facilityName = record?.facilityName || "Facility";
  const plan = record?.selectedPlan || "Plan";
  const frequency = record?.billingFrequency || "Monthly";
  const paymentMethod = record?.paymentMethod?.trim() || "—";

  const periodEndRaw =
    record?.planEndDate || record?.planEndDateIso || record?.expiryDate;

  const archivedUntil = useMemo(() => {
    const end = parseDate(periodEndRaw);
    if (!end) return "—";
    return addMonths(end, 6);
  }, [periodEndRaw]);

  const archivedUntilLabel =
    archivedUntil instanceof Date ? formatShortDate(archivedUntil) : "—";

  const daysLeft =
    archivedUntil instanceof Date ? daysUntil(archivedUntil) : null;

  const billingStart = useMemo(() => new Date(), [open]);

  const nextBillingDate = useMemo(() => {
    const isYearly = frequency.toLowerCase() === "yearly";
    return addMonths(billingStart, isYearly ? 12 : 1);
  }, [billingStart, frequency]);

  const billingStartLabel = `Today, ${formatShortDate(billingStart)}`;

  const detailRows = [
    { label: "Facility", value: facilityName },
    { label: "Plan", value: `${plan} (${frequency})` },
    { label: "Billing start", value: billingStartLabel },
    { label: "Next billing date", value: formatShortDate(nextBillingDate) },
    {
      label: "Payment method",
      value: paymentMethod,
      action: "Change",
    },
    { label: "Access restored", value: "Immediately", highlight: true },
  ] as const;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!submitting) onBackToBilling();
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          fontFamily: '"Rethink Sans", sans-serif',
          maxHeight: "92vh",
          position: "relative",
        },
      }}
    >
      {submitting && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.72)",
            borderRadius: "12px",
          }}
        >
          <CircularProgress size={36} sx={{ color: "#0a829d" }} />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          px: { xs: 2.5, sm: 3 },
          pt: 2.5,
          pb: 1,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
            <NoteAddOutlinedIcon sx={{ color: "#0a829d", fontSize: 22 }} />
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#065b70",
                lineHeight: 1.3,
              }}
            >
              Resubscribe
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "14px", color: "#525252", lineHeight: 1.5 }}>
            Review the details below and confirm to restore access immediately.
          </Typography>
        </Box>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 2.5, sm: 3 },
          pt: 0,
          pb: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          overflow: "visible",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#f3fdff",
            border: "1px solid #99d4e1",
            borderRadius: "8px",
            px: 1.75,
            py: 1.5,
          }}
        >
          <Typography sx={{ fontSize: "13px", color: "#065b70", lineHeight: 1.45 }}>
            Your archived data will be permanently deleted on {archivedUntilLabel}
            {daysLeft != null ? ` — ${daysLeft} days from now` : ""}. Resubscribing
            today restores everything immediately.
          </Typography>
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 700,
              color: "#065b70",
              mb: 1.5,
            }}
          >
            What will be restored
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr 1fr",
                sm: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.25,
            }}
          >
            {RESTORE_STATS.map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  border: "1px solid #cfeaf0",
                  borderRadius: "8px",
                  backgroundColor: "#fdffff",
                  px: 1.5,
                  py: 1.5,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "22px",
                    fontWeight: 700,
                    color: "#0a829d",
                    lineHeight: 1.2,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  sx={{ fontSize: "12px", color: "#525252", mt: 0.5, lineHeight: 1.3 }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 700,
              color: "#065b70",
              mb: 1.5,
            }}
          >
            Subscription details
          </Typography>
          <Box
            sx={{
              border: "1px solid #d8eff4",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            {detailRows.map((row, index) => (
              <Box
                key={row.label}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: { xs: 0.25, sm: 2 },
                  px: 2,
                  py: 1.25,
                  backgroundColor: index % 2 === 0 ? "#f8fdfe" : "#ffffff",
                  borderBottom:
                    index < detailRows.length - 1 ? "1px solid #e5f5f8" : "none",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: "13px", color: "#737373" }}>
                  {row.label}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "flex-start", sm: "flex-end" },
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "highlight" in row && row.highlight ? "#0a829d" : "#262626",
                      textAlign: { xs: "left", sm: "right" },
                    }}
                  >
                    {row.value}
                  </Typography>
                  {"action" in row && row.action && (
                    <Link
                      component="button"
                      type="button"
                      onClick={onChangePaymentMethod}
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#0a829d",
                        textDecoration: "none",
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {row.action}
                    </Link>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            justifyContent: "space-between",
            pt: 0.5,
          }}
        >
          <Button
            type="button"
            variant="outlined"
            onClick={onBackToBilling}
            disabled={submitting}
            startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              fontFamily: '"Rethink Sans", sans-serif',
              fontWeight: 600,
              fontSize: "14px",
              color: "#404040",
              borderColor: "#b3b3b3",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              px: 3,
              py: 1.1,
              flex: { xs: "1 1 100%", sm: "0 1 auto" },
              "&:hover": {
                borderColor: "#8a8a8a",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Back to billing
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={16} sx={{ color: "#e6fafc" }} />
              ) : (
                <NoteAddOutlinedIcon sx={{ fontSize: 18 }} />
              )
            }
            sx={{
              textTransform: "none",
              fontFamily: '"Rethink Sans", sans-serif',
              fontWeight: 600,
              fontSize: "14px",
              color: "#e6fafc",
              background: submitting
                ? "#7eb8c4"
                : "linear-gradient(to right, #0a829d, #58c2d8)",
              borderRadius: "8px",
              px: 3,
              py: 1.1,
              flex: { xs: "1 1 100%", sm: "0 1 auto" },
              boxShadow: "none",
              "&:hover": {
                background: submitting
                  ? "#7eb8c4"
                  : "linear-gradient(to right, #08708a, #4ab0c6)",
              },
              "&.Mui-disabled": {
                color: "#e6fafc",
                background: "#7eb8c4",
              },
            }}
          >
            {submitting ? "Resubscribing..." : "Confirm resubscription"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ResubscribeDialog;
